import { BaseService } from './base.service';
import { CustomResponseEntity, PaginationParams } from '../types/common';
import { InspectionDto } from '../types/dtos/inspection';
import { api } from '../client/axiosClient';

/**
 * InspectionService handles CRUD operations for inspections.
 *
 * URL Pattern: /api/v1/inspections
 * Organization context is provided via X-Organization-Id header (injected by axiosClient).
 *
 * Migration: The organizationId parameter has been removed from URL paths. The backend
 * extracts organization context from the X-Organization-Id header for tenant scoping.
 *
 * @deprecated organizationId constructor parameter is deprecated and no longer used.
 * Pass empty string or undefined for backward compatibility.
 */
export class InspectionService extends BaseService<InspectionDto> {
  private basePath = '/api/v1/inspections';

  /**
   * @deprecated organizationId is no longer used. Organization context comes from X-Organization-Id header.
   */
  constructor(_organizationId?: string) {
    super('/api/v1/inspections');
  }

  findAll(params?: PaginationParams): Promise<CustomResponseEntity<InspectionDto[]>> {
    return api.get(this.basePath, { params });
  }

  findById(id: string): Promise<CustomResponseEntity<InspectionDto>> {
    return api.get(`${this.basePath}/${id}`);
  }

  create(data: Partial<InspectionDto>): Promise<CustomResponseEntity<InspectionDto>> {
    return api.post(this.basePath, data);
  }

  update(id: string, data: Partial<InspectionDto>): Promise<CustomResponseEntity<InspectionDto>> {
    return api.put(`${this.basePath}/${id}`, data);
  }

  delete(id: string): Promise<CustomResponseEntity<void>> {
    return api.delete(`${this.basePath}/${id}`);
  }

  findByProgram(programId: string): Promise<CustomResponseEntity<InspectionDto[]>> {
    return api.get(`${this.basePath}/program/${programId}`);
  }

  findByFarm(farmId: string): Promise<CustomResponseEntity<InspectionDto[]>> {
    return api.get(`${this.basePath}/farm/${farmId}`);
  }

  findByInspector(inspectorId: string): Promise<CustomResponseEntity<InspectionDto[]>> {
    return api.get(`${this.basePath}/inspector/${inspectorId}`);
  }

  updateStatus(id: string, status: string): Promise<CustomResponseEntity<InspectionDto>> {
    return api.put(`${this.basePath}/${id}/status`, { status });
  }
}

/**
 * Create an InspectionService instance.
 * @deprecated organizationId is no longer used. Organization context comes from X-Organization-Id header.
 */
export const createInspectionService = (_organizationId?: string) => new InspectionService(_organizationId); 